import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminNavigation } from "@/lib/adminNavigation";
import { APP_VERSION } from "@/lib/appMetadata";
import axios from "axios";
import { API } from "@/App";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      const { data } = await axios.get(`${API}/network/status`, { withCredentials: true });
      if (data.csrf_token) await axios.delete(`${API}/network/session`, {
        withCredentials: true, headers: { "X-Network-CSRF": data.csrf_token },
      });
    } catch { /* Logout remains available when network management is disabled. */ }
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
    navigate("/login");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col" data-testid="layout-container" style={{ background: '#F5F5F5' }}>
      {/* Mobile Header */}
      <header className="lg:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg, #DC4444 0%, #B91C1C 100%)' }}>
            <img src="/logo.png" alt="Logo" className="w-full h-full object-contain p-1" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-gray-900 leading-tight">Payment Management</h1>
            <p className="text-xs text-gray-600">Feuerwehr Kantine</p>
          </div>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg, #DC4444 0%, #B91C1C 100%)' }}>
                <img src="/logo.png" alt="Logo" className="w-full h-full object-contain p-1" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">Payment Management</h1>
                <p className="text-xs text-gray-600">Feuerwehr Kantine</p>
              </div>
            </div>

            <nav className="space-y-2">
              {adminNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeMobileMenu}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="p-6 border-t border-gray-200">
            <Button
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex w-64 bg-white border-r border-gray-200 shadow-sm flex-col">
          <div className="flex-1 p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg, #DC4444 0%, #B91C1C 100%)' }}>
                <img src="/logo.png" alt="Logo" className="w-full h-full object-contain p-1" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">Payment Management</h1>
                <p className="text-xs text-gray-600">Feuerwehr Kantine</p>
              </div>
            </div>

            <nav className="space-y-2">
              {adminNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="p-6 border-t border-gray-200">
            <Button
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto" style={{ background: '#F5F5F5' }}>
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 text-center text-xs sm:text-sm text-gray-600">
        © 2025–2026 CreativeyMedia | v{APP_VERSION}
      </footer>
    </div>
  );
};

export default Layout;
